// src/pages/allKanji.js
// Public kanji index (SEO Phase 2a). Renders entirely from bundled static data
// (src/data/kanjiStatic.json, generated from kanjiMasterList.csv at build time),
// so it prerenders to real HTML with no API call. Search is the only runtime/API
// feature and is inert during prerender (no query typed).
import React, { useEffect, useState } from 'react';
import { usePostgresUser } from '../components/utils/PostgresUserContext';
import Navbar from '../components/navBar';
import Footer from '../components/footer';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { useKanjiSearch } from '../services/hooks/useKanjiSearch';
import { MdClear } from 'react-icons/md';
import kanjiStatic from '../data/kanjiStatic.json';

const LEVELS_PER_PAGE = 5;
const TOTAL_LEVELS = kanjiStatic.maxLevel;
const TOTAL_PAGES = Math.ceil(TOTAL_LEVELS / LEVELS_PER_PAGE);

// 48 level buttons in one block was a wall; pick a range of 10, then a level.
// Every level link stays in the prerendered HTML (all ranges render, the
// inactive ones are hidden), so crawlers still find each /levels/N page.
const LEVEL_RANGES = [];
for (let start = 1; start <= TOTAL_LEVELS; start += 10) {
  LEVEL_RANGES.push([start, Math.min(start + 9, TOTAL_LEVELS)]);
}

function LevelRangePicker() {
  const [active, setActive] = useState(() => {
    const saved = typeof window !== 'undefined' ? Number(sessionStorage.getItem('allKanjiLevelRange')) : 0;
    return saved > 0 && saved <= LEVEL_RANGES.length ? saved - 1 : 0;
  });
  const choose = (i) => {
    setActive(i);
    try { sessionStorage.setItem('allKanjiLevelRange', String(i + 1)); } catch (e) { /* ignore */ }
  };
  return (
    <div className="bg-white border rounded-xl p-3 sm:p-4">
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 max-sm:grid max-sm:grid-cols-5 max-sm:overflow-visible max-sm:mx-0 max-sm:px-0" role="tablist" aria-label="Level ranges">
        {LEVEL_RANGES.map(([from, to], i) => (
          <button
            key={from}
            type="button"
            role="tab"
            aria-selected={i === active}
            onClick={() => choose(i)}
            className={`shrink-0 px-3 py-2 min-h-[40px] max-sm:px-0 max-sm:text-xs rounded-lg text-sm font-semibold transition-colors duration-200 ${
              i === active ? 'bg-logoColor text-white' : 'bg-lightBlueBackground/50 text-logoColor hover:bg-lightBlueBackground'
            }`}
          >
            {from}–{to}
          </button>
        ))}
      </div>
      {LEVEL_RANGES.map(([from, to], i) => (
        <div key={from} role="tabpanel" hidden={i !== active} className={`${i === active ? 'grid' : 'hidden'} grid-cols-5 gap-2 mt-3`}>
          {Array.from({ length: to - from + 1 }, (_, k) => from + k).map((n) => (
            <Link
              key={n}
              to={`/levels/${n}`}
              className="flex flex-col items-center justify-center min-h-[52px] rounded-lg border text-logoColor hover:bg-blue-100 transition duration-300"
            >
              <span className="text-[10px] uppercase tracking-wider text-gray-500 leading-none">Level</span>
              <span className="text-lg font-bold leading-tight">{n}</span>
            </Link>
          ))}
        </div>
      ))}
    </div>
  );
}

function AllKanji() {
  const { user, subscriptionStatus } = usePostgresUser();

  const [currentPage, setCurrentPage] = useState(() => {
    const saved = typeof window !== 'undefined' ? sessionStorage.getItem('allKanjiCurrentPage') : null;
    return saved ? parseInt(saved, 10) : 1;
  });

  const {
    results: searchResults,
    isLoading: isSearching,
    error: searchError,
    query: searchQuery,
    setQuery: setSearchQuery,
    clear: clearSearch,
    manualSearch,
  } = useKanjiSearch('', { debounceMs: 300, minChars: 1, limit: 20 });

  const inSearchMode = Boolean(
    searchQuery &&
      searchQuery.trim().length >= 1 &&
      (isSearching || (Array.isArray(searchResults) && searchResults.length > 0))
  );

  useEffect(() => {
    sessionStorage.setItem('allKanjiCurrentPage', currentPage.toString());
  }, [currentPage]);

  const startLevel = (currentPage - 1) * LEVELS_PER_PAGE + 1;
  const endLevel = Math.min(startLevel + LEVELS_PER_PAGE - 1, TOTAL_LEVELS);
  const levelsToShow = [];
  for (let l = startLevel; l <= endLevel; l++) levelsToShow.push(l);

  return (
    <div className="font-noto-sans-jp">
      <Helmet>
        <title>{`All Kanji — Browse ${kanjiStatic.total} Japanese Kanji by Level & JLPT | OniKanji`}</title>
        <meta name="description" content={`Browse all ${kanjiStatic.total} kanji in the OniKanji curriculum across ${TOTAL_LEVELS} levels and JLPT N5–N1. Look up the meaning of every Japanese kanji, free.`} />
        <link rel="canonical" href="https://onikanji.com/kanji" />
        <meta property="og:title" content="Browse All Kanji by Level & JLPT | OniKanji" />
        <meta property="og:description" content={`Browse all ${kanjiStatic.total} kanji across ${TOTAL_LEVELS} levels and JLPT N5–N1.`} />
        <meta property="og:image" content="https://onikanji.com/og-image.png" />
        <meta property="og:url" content="https://onikanji.com/kanji" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="OniKanji" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Browse All Kanji by Level & JLPT | OniKanji" />
        <meta name="twitter:image" content="https://onikanji.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'All Kanji',
          url: 'https://onikanji.com/kanji',
          description: `Browse all ${kanjiStatic.total} kanji by level and JLPT.`,
          isPartOf: { '@type': 'WebSite', name: 'OniKanji', url: 'https://onikanji.com' },
        })}</script>
      </Helmet>

      <Navbar user={user} subscriptionStatus={subscriptionStatus} />

      <div className="container mx-auto space-y-4 p-6 max-sm:px-4 max-sm:py-4 sm:max-lg:!px-5 sm:max-lg:!py-4">
        <h1 className="text-3xl max-sm:text-2xl md:text-4xl font-bold text-logoColor text-center mt-6 max-sm:mt-2">All Kanji</h1>
        <p className="text-center text-gray-600 max-w-2xl mx-auto max-sm:text-sm">
          Browse every kanji in the OniKanji curriculum — {kanjiStatic.total} kanji across {TOTAL_LEVELS} levels,
          graded by JLPT. Select any kanji to see its meaning and readings.
        </p>

        {/* ===== Search bar ===== */}
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row w-full">
            <label
              htmlFor="kanji-search"
              className="text-base font-semibold text-gray-700 bg-lightBlueBackground px-4 py-3 rounded-t-lg sm:rounded-l-lg sm:rounded-tr-none flex-shrink-0 text-center sm:text-left"
            >
              Search Kanji
            </label>
            <div className="relative flex-1 min-w-0">
              <input
                id="kanji-search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    manualSearch();
                  } else if (e.key === 'Escape') {
                    clearSearch();
                  }
                }}
                placeholder="Try: 水, water, みず, sui, 人, person..."
                aria-label="Search Kanji"
                className="w-full px-4 py-2.5 text-base font-medium rounded-b-lg sm:rounded-r-lg sm:rounded-bl-none focus:outline-none transition-all duration-300
          bg-mainBackgroundColor border-2 border-t-0 sm:border-t-2 sm:border-l-0 border-lightBlueBackground text-logoColor
          focus:border-oniKanjiPink focus:shadow-lg focus:shadow-pink-300/40"
              />
              <button
                type="button"
                onClick={clearSearch}
                disabled={!searchQuery.trim()}
                aria-label="Clear search"
                className={`absolute right-1 top-1/2 transform -translate-y-1/2 p-2 rounded-lg transition-all duration-300 flex items-center justify-center
          ${
            !searchQuery.trim()
              ? 'invisible'
              : isSearching
              ? 'text-white bg-gray-500'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
          }`}
              >
                {isSearching ? <ClipLoader color="white" size={16} /> : <MdClear className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <div className="text-sm max-sm:text-xs text-gray-600 bg-gray-50 rounded-lg p-3">
            <strong>You can search by:</strong> Kanji characters (水), English meanings (water), Hiragana readings (みず), or kunyomi/onyomi readings using hiragana
          </div>
          <div aria-live="polite" className="text-sm text-gray-600 mt-1">
            {isSearching
              ? 'Searching...'
              : searchError
              ? `Search error: ${searchError}`
              : Array.isArray(searchResults) && searchResults.length > 0
              ? `Found ${searchResults.length} kanji matching "${searchQuery}"`
              : searchQuery.trim().length >= 1
              ? `No results for "${searchQuery}"`
              : ''}
          </div>
        </div>

        {inSearchMode ? (
          // Search results view
          <div className="mt-8 max-sm:mt-4 grid grid-cols-2 max-sm:grid-cols-3 max-sm:gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:max-md:!grid-cols-5 md:max-lg:!grid-cols-6 sm:max-lg:!gap-3">
            {searchResults && searchResults.length > 0 ? (
              searchResults.map((kanji) => (
                <Link
                  to={`/kanji/${kanji.kanji}`}
                  key={kanji.kanji}
                  className="flex flex-col items-center p-4 max-sm:px-1.5 max-sm:py-3 max-sm:min-w-0 sm:max-lg:!px-2 sm:max-lg:!py-3 sm:max-lg:min-w-0 bg-white border rounded-lg shadow hover:bg-blue-100 transition duration-300"
                >
                  <div className="text-4xl max-sm:text-3xl max-sm:mb-1 font-bold mb-2">{kanji.kanji}</div>
                  <div className="text-gray-600 max-sm:text-xs max-sm:w-full max-sm:text-center max-sm:truncate sm:max-lg:w-full sm:max-lg:text-center sm:max-lg:truncate sm:max-lg:!text-sm">{kanji.primaryMeaning}</div>
                </Link>
              ))
            ) : (
              <p className="col-span-full text-center text-gray-500">No kanji found.</p>
            )}
          </div>
        ) : (
          <>
            {/* ===== Browse directories (crawlable links to every level & JLPT hub) ===== */}
            <div className="pt-4">
              <h2 className="text-xl font-bold text-logoColor mb-3">Browse by JLPT level</h2>
              <div className="flex flex-wrap gap-3">
                {kanjiStatic.jlptLevels.map((j) => (
                  <Link
                    key={j}
                    to={`/jlpt/${j.toLowerCase()}`}
                    className="px-4 py-2 max-sm:px-3 max-sm:py-2.5 rounded-lg bg-lightBlueBackground text-lightBlueText font-semibold hover:bg-blue-100 transition duration-300"
                  >
                    JLPT {j} <span className="text-sm font-normal">({kanjiStatic.counts.byJlpt[j]})</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="pt-2">
              <h2 className="text-xl font-bold text-logoColor mb-3">Browse by level</h2>
              <LevelRangePicker />
            </div>

            {/* ===== Paginated level grid ===== */}
            {levelsToShow.map((level) => (
              <React.Fragment key={level}>
                <div className="flex flex-col items-center bg-lightBlueBackground text-lightBlueText p-4 max-sm:py-3 rounded-lg mt-10 max-sm:mt-6 sm:max-lg:!mt-6 sm:max-lg:!py-3">
                  <div className="text-2xl max-sm:text-xl font-bold">Level {level} Kanji</div>
                  <div className="text-lg">{(kanjiStatic.byLevel[level] || []).length} kanji</div>
                </div>
                <div className="grid grid-cols-2 max-sm:grid-cols-3 max-sm:gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:max-md:!grid-cols-5 md:max-lg:!grid-cols-6 sm:max-lg:!gap-3 mt-6">
                  {(kanjiStatic.byLevel[level] || []).map((k) => (
                    <Link
                      to={`/kanji/${k.c}`}
                      key={k.c}
                      className="flex flex-col items-center p-4 max-sm:px-1.5 max-sm:py-3 max-sm:min-w-0 sm:max-lg:!px-2 sm:max-lg:!py-3 sm:max-lg:min-w-0 bg-white border rounded-lg shadow hover:bg-blue-100 transition duration-300"
                    >
                      <div className="text-4xl max-sm:text-3xl max-sm:mb-1 font-bold mb-2">{k.c}</div>
                      <div className="text-gray-600 max-sm:text-xs max-sm:w-full max-sm:text-center max-sm:truncate sm:max-lg:w-full sm:max-lg:text-center sm:max-lg:truncate sm:max-lg:!text-sm">{k.m}</div>
                    </Link>
                  ))}
                </div>
              </React.Fragment>
            ))}

            <div className="flex justify-between mt-6">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded ${currentPage === 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 text-white'}`}
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, TOTAL_PAGES))}
                disabled={currentPage === TOTAL_PAGES}
                className={`px-4 py-2 rounded ${currentPage === TOTAL_PAGES ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 text-white'}`}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default AllKanji;
